import {InvoicePrintItem, InvoicePrintModel} from './_models'
import {formatNumber, formatPercent, hasAmount, isNumberText} from './printNumbers'
import {persianText} from './printText'

// The columns of the invoice rows. Titles are those of RC_IITP.IS_V7.9 table 2. A column is shown
// only when its official field is not out of pattern for the invoice (printableFields, decided by
// the server from the rule matrix) and, for the optional ones, when a row has a value.

export type PrintColumnKind = 'index' | 'code' | 'text' | 'number' | 'amount' | 'rate'

export type PrintColumn = {
  key: string
  /** The official JSON name whose pattern status decides; empty for the row number and description. */
  field: string
  title: string
  kind: PrintColumnKind
  /** Shown whenever the field is in pattern, even if every row is empty or zero. */
  always?: boolean
  /** Added up in the page and running totals at the foot of every sheet. */
  sum?: boolean
  value: (item: InvoicePrintItem) => string | null
  /** Smaller second line in the cell (subject under other taxes, currency under its amount…). */
  secondary?: (item: InvoicePrintItem) => string | null
  /** A short text after the value on the same line (the unit after the quantity). */
  suffix?: (item: InvoicePrintItem) => string | null
  /** Overrides the default "in pattern and has a value" rule. */
  visible?: (invoice: InvoicePrintModel) => boolean
}

const joinParts = (...parts: (string | null | undefined)[]) =>
  parts.filter((part) => part && part.trim()).join(' ، ') || null

const subjectAndRate = (title: string | null, rate: string | null) =>
  joinParts(title ? persianText(title) : null, rate ? formatPercent(rate) : null)

const singleCurrency = (invoice: InvoicePrintModel) =>
  new Set(invoice.items.map((item) => item.currencyCode).filter(Boolean)).size <= 1

export const PRINT_COLUMNS: PrintColumn[] = [
  {
    key: 'row',
    field: '',
    title: 'ردیف',
    kind: 'index',
    always: true,
    value: (item) => String(item.row),
  },
  {
    key: 'stuffId',
    field: 'sstid',
    title: 'شناسه کالا/خدمت',
    kind: 'code',
    always: true,
    value: (item) => item.stuffId,
  },
  {
    key: 'description',
    field: '',
    title: 'شرح کالا/خدمت',
    kind: 'text',
    always: true,
    value: (item) => (item.title ? persianText(item.title) : null),
    secondary: (item) =>
      joinParts(
        item.subtitle ? persianText(item.subtitle) : null,
        item.thirdPartyContract
          ? `شماره قرارداد معامله از طریق شخص ثالث: ${item.thirdPartyContract}`
          : null
      ),
  },
  {
    key: 'quantity',
    field: 'am',
    title: 'تعداد/مقدار',
    kind: 'number',
    always: true,
    value: (item) => item.quantity,
    // The unit (mu) on the quantity's line: a column of its own would cost an A4 portrait sheet
    // about 13 mm of the description's width.
    suffix: (item) => (item.unit ? persianText(item.unit) : null),
  },
  {
    key: 'netWeight',
    field: 'nw',
    title: 'وزن خالص',
    kind: 'number',
    sum: true,
    value: (item) => item.netWeight,
  },
  {
    key: 'unitPrice',
    field: 'fee',
    title: 'مبلغ واحد',
    kind: 'amount',
    value: (item) => item.unitPrice,
    // Export rows store their total rial value as the unit amount; the rial and currency values
    // are the export columns, as on the earlier print.
    visible: (invoice) => invoice.pattern !== 7,
  },
  {
    key: 'currencyAmount',
    field: 'cfee',
    title: 'میزان ارز',
    kind: 'number',
    value: (item) => item.currencyAmount,
    secondary: (item) => item.currencyCode,
  },
  {
    key: 'exchangeRate',
    field: 'exr',
    title: 'نرخ برابری ارز با ریال',
    kind: 'amount',
    value: (item) => item.exchangeRate,
    secondary: (item) => (item.currencyAmount ? null : item.currencyCode),
  },
  {
    key: 'purchaseRate',
    field: 'cpr',
    title: 'نرخ خرید ارز',
    kind: 'number',
    value: (item) => item.purchaseRate,
  },
  {
    key: 'rialValue',
    field: 'ssrv',
    title: 'ارزش ریالی کالا',
    kind: 'amount',
    sum: true,
    value: (item) => item.rialValue,
  },
  {
    key: 'currencyValue',
    field: 'sscv',
    title: 'ارزش ارزی کالا',
    kind: 'number',
    sum: true,
    value: (item) => item.currencyValue,
    secondary: (item) => item.currencyCode,
  },
  {key: 'carat', field: 'cui', title: 'عیار', kind: 'number', value: (item) => item.carat},
  {
    key: 'constructionFee',
    field: 'consfee',
    title: 'اجرت ساخت',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.constructionFee,
  },
  {
    key: 'sellerProfit',
    field: 'spro',
    title: 'سود فروشنده',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.sellerProfit,
  },
  {
    key: 'commission',
    field: 'bros',
    title: 'حق‌العمل',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.commission,
  },
  {
    key: 'goldTotal',
    field: 'tcpbs',
    title: 'جمع کل اجرت، حق‌العمل و سود',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.goldTotal,
  },
  {
    key: 'amountBeforeDiscount',
    field: 'prdis',
    title: 'مبلغ قبل از تخفیف',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.amountBeforeDiscount,
  },
  {
    key: 'discount',
    field: 'dis',
    title: 'مبلغ تخفیف',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.discount,
  },
  {
    key: 'amountAfterDiscount',
    field: 'adis',
    title: 'مبلغ بعد از تخفیف',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.amountAfterDiscount,
  },
  {
    key: 'vatSource',
    field: 'sovat',
    title: 'ماخذ مالیات بر ارزش افزوده',
    kind: 'amount',
    sum: true,
    value: (item) => item.vatSource,
  },
  {
    key: 'vatBase',
    field: 'vba',
    title: 'مبلغ پایه مالیات بر ارزش افزوده',
    kind: 'amount',
    sum: true,
    value: (item) => item.vatBase,
  },
  {
    key: 'vatRate',
    field: 'vra',
    title: 'نرخ مالیات بر ارزش افزوده',
    kind: 'rate',
    always: true,
    value: (item) => item.vatRate,
  },
  {
    key: 'vatAmount',
    field: 'vam',
    title: 'مبلغ مالیات بر ارزش افزوده',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.vatAmount,
  },
  {
    key: 'otherTax',
    field: 'odam',
    title: 'سایر مالیات و عوارض',
    kind: 'amount',
    sum: true,
    value: (item) => item.otherTaxAmount,
    secondary: (item) => subjectAndRate(item.otherTaxTitle, item.otherTaxRate),
    visible: (invoice) => invoice.items.some((item) => hasAmount(item.otherTaxAmount)),
  },
  {
    key: 'legalFund',
    field: 'olam',
    title: 'سایر وجوه قانونی',
    kind: 'amount',
    sum: true,
    value: (item) => item.legalFundAmount,
    secondary: (item) => subjectAndRate(item.legalFundTitle, item.legalFundRate),
    visible: (invoice) => invoice.items.some((item) => hasAmount(item.legalFundAmount)),
  },
  {
    key: 'total',
    field: 'tsstam',
    title: 'مبلغ کل کالا/خدمت',
    kind: 'amount',
    always: true,
    sum: true,
    value: (item) => item.total,
  },
]

/** The columns this invoice prints, in order. */
export const resolveColumns = (invoice: InvoicePrintModel): PrintColumn[] => {
  const printable = new Set(invoice.printableFields)
  return PRINT_COLUMNS.filter((column) => {
    if (column.field && !printable.has(column.field)) return false
    if (column.visible && !column.visible(invoice)) return false
    if (column.always || column.visible) return true
    return invoice.items.some((item) => {
      const value = column.value(item)
      return value !== null && value !== undefined && value.trim() !== ''
    })
  }).map((column) =>
    // Currency values of different currencies do not add up.
    column.key === 'currencyValue' && !singleCurrency(invoice) ? {...column, sum: false} : column
  )
}

/** The text a cell shows for its kind. */
export const formatCell = (column: PrintColumn, raw: string | null): string => {
  if (raw === null || raw === undefined || raw === '') return ''
  switch (column.kind) {
    case 'number':
    case 'amount':
      return isNumberText(raw) ? formatNumber(raw) : raw
    case 'rate':
      return formatPercent(raw)
    default:
      return raw
  }
}
